/**
 * Shared helpers for creating SkillPurchase and TalentPurchase records,
 * and normalizing skill/talent objects with required fields.
 *
 * Eliminates duplicated object construction across:
 * - SkillsService, TalentsService
 * - createSkillsSlice, createTalentsSlice, createAdvancementSlice
 */

import type { Skill, SkillPurchase, Talent, TalentPurchase } from '../types/character';

// ============================================================================
// Purchase Record Factories
// ============================================================================

/**
 * Create a SkillPurchase record with standard fields.
 */
export function createSkillPurchase(
  advancementId: string,
  rank: number,
  bonus: number
): SkillPurchase {
  return {
    advancementId,
    purchasedAt: new Date(),
    rank,
    bonus
  };
}

/**
 * Create a TalentPurchase record with standard fields.
 */
export function createTalentPurchase(
  advancementId: string,
  rank: number,
  affectedSkillId?: string
): TalentPurchase {
  return {
    advancementId,
    purchasedAt: new Date(),
    rank,
    ...(affectedSkillId && { affectedSkillId })
  };
}

// ============================================================================
// Normalization Helpers
// ============================================================================

/**
 * Ensure a skill has purchaseHistory initialized.
 * Critical for self-contained character data.
 */
export function ensureSkillPurchaseHistory(skill: Skill): Skill {
  return {
    ...skill,
    purchaseHistory: skill.purchaseHistory || []
  };
}

/**
 * Ensure a talent has purchaseHistory and purchaseCount initialized.
 * Critical for self-contained character data.
 */
export function ensureTalentFields(talent: Talent): Talent {
  return {
    ...talent,
    purchaseHistory: talent.purchaseHistory || [],
    purchaseCount: talent.purchaseCount ?? (talent.purchaseHistory?.length || 0)
  };
}
